// ============================================================================
// Capability Registry — adapter ↔ capability lookup.
//
// Adapters call `register(typeId, capability, impl)` at module init. Modules
// later call `resolve(instanceId, capability)` which:
//   1. Loads the instance row to learn its typeId
//   2. Looks up the implementation registered for (typeId, capability)
//   3. Returns the impl + the instance row (companyId etc.)
//
// Registration lives in module-init code for the adapter file (e.g.
// `adapters/facebook-messenger.ts`). The registry is an in-isolate Map; on
// Worker cold-start the adapter modules re-register themselves at import
// time. No DB persistence is needed because the mapping is code, not data.
// ============================================================================

import { and, eq, isNull } from 'drizzle-orm'
import type { DrizzleD1Database } from 'drizzle-orm/d1'
import { connectorInstances, type ConnectorInstance } from '../schemas/connectors-instances'
import { connectorTypes } from '../schemas/connectors-types'
import type {
  CapabilityName,
  CapabilityMethodName,
  CapabilityMethodArgs,
  CapabilityMethodResult,
} from '../capabilities'

// ============================================================================
// Adapter shape
// ============================================================================

/**
 * Runtime context handed to every adapter method invocation. Adapters use it
 * to talk to the provider with the right credentials, log against the
 * tenant, etc.
 *
 * Note: refresh tokens are intentionally NOT exposed. Token lifecycle is
 * owned by the dispatcher's refresh-coordinator path; adapters that need a
 * fresh access token after a 401 should throw and let the next dispatch
 * retry with a refreshed token.
 */
export interface AdapterContext {
  instanceId: string
  companyId: string
  /** Decrypted access token, ready to send to the provider. */
  accessToken: string
  /** Optional opaque per-instance config (`connector_instances.config` JSON). */
  config?: unknown
  /** Optional opaque per-instance metadata (`connector_instances.metadata` JSON). */
  metadata?: unknown
}

/**
 * Adapter implementation for a single capability. Each method gets the
 * caller-supplied args plus the runtime context.
 */
export type AdapterMethods<C extends CapabilityName> = {
  [M in CapabilityMethodName<C>]: (
    args: CapabilityMethodArgs<C, M>,
    ctx: AdapterContext,
  ) => Promise<CapabilityMethodResult<C, M>>
}

// ============================================================================
// Registry storage
// ============================================================================

// typeId → capability name → adapter methods
const registry = new Map<string, Map<CapabilityName, AdapterMethods<CapabilityName>>>()

/**
 * Register an adapter implementation for `(typeId, capability)`. Idempotent —
 * later registrations overwrite earlier ones (useful for hot-swap in tests).
 */
export function register<C extends CapabilityName>(
  typeId: string,
  capability: C,
  methods: AdapterMethods<C>,
): void {
  if (!typeId) throw new RegistryError('INVALID_TYPE_ID', 'typeId is required')
  if (!registry.has(typeId)) {
    registry.set(typeId, new Map())
  }
  registry.get(typeId)!.set(capability, methods as AdapterMethods<CapabilityName>)
}

/**
 * Look up the adapter methods for a `(typeId, capability)` directly. Most
 * consumer code should use `resolve(instanceId, capability)` instead, which
 * also returns the instance row for context building.
 */
export function lookup<C extends CapabilityName>(
  typeId: string,
  capability: C,
): AdapterMethods<C> | null {
  const byCap = registry.get(typeId)
  if (!byCap) return null
  return (byCap.get(capability) as AdapterMethods<C>) ?? null
}

/**
 * Resolve `(instanceId, capability)` for a given `companyId` to the adapter
 * methods + the loaded instance row. Returns `null` if any of:
 *   - instanceId does not exist or belongs to a different company
 *   - the instance is soft-deleted
 *   - no adapter is registered for the instance's type
 *
 * companyId is REQUIRED — multi-tenancy invariant. Callers must propagate
 * the authenticated company from the request context; never trust the
 * client to identify which company they belong to.
 */
export async function resolve<C extends CapabilityName>(
  instanceId: string,
  capability: C,
  companyId: string,
  db: DrizzleD1Database,
): Promise<{ methods: AdapterMethods<C>; instance: ConnectorInstance } | null> {
  if (!instanceId) throw new RegistryError('INVALID_INSTANCE_ID', 'instanceId is required')
  if (!companyId) throw new RegistryError('INVALID_INSTANCE_ID', 'companyId is required')

  const instance = await db
    .select()
    .from(connectorInstances)
    .where(
      and(
        eq(connectorInstances.id, instanceId),
        eq(connectorInstances.companyId, companyId),
        isNull(connectorInstances.deletedAt),
      ),
    )
    .get()

  if (!instance) return null

  let methods = lookup(instance.typeId, capability)
  if (!methods) {
    const type = await db
      .select({ slug: connectorTypes.slug })
      .from(connectorTypes)
      .where(eq(connectorTypes.id, instance.typeId))
      .get()
    if (type?.slug && type.slug !== instance.typeId) {
      methods = lookup(type.slug, capability)
    }
  }
  if (!methods) return null

  return { methods, instance }
}

/**
 * List capability names registered for a given type id (debug / introspection).
 */
export function capabilitiesFor(typeId: string): CapabilityName[] {
  const byCap = registry.get(typeId)
  return byCap ? Array.from(byCap.keys()) : []
}

/**
 * Test hook — wipe all registrations. Do NOT call in production.
 * @internal
 */
export function __resetRegistry(): void {
  registry.clear()
}

/**
 * Test hook — count entries. @internal
 */
export function __registrySize(): number {
  let count = 0
  for (const byCap of registry.values()) count += byCap.size
  return count
}

// ============================================================================
// Errors
// ============================================================================

export type RegistryErrorCode = 'INVALID_TYPE_ID' | 'INVALID_INSTANCE_ID'

export class RegistryError extends Error {
  constructor(public readonly code: RegistryErrorCode, message: string) {
    super(message)
    this.name = 'RegistryError'
  }
}
